import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { FeatureCard } from "@/components/marketing/FeatureCard";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  Building2,
  Network,
  TrendingUp,
  Users,
  CheckCircle,
  Shield,
  FileCheck,
  Clock,
  DollarSign,
  Zap,
  GitCompare,
  Eye,
  ArrowRight,
} from "lucide-react";

export default function ForAgencies() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="For Agencies - Streamline Client Production"
        description="Access a verified vendor network through one platform. Manage client production with better margins, less complexity, and complete visibility."
        canonical="/for-agencies"
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            Streamline <span className="text-primary">Client Production</span> Management
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Access our vendor marketplace through one platform—no juggling multiple relationships, portals, or processes. 
            Manage all your client production with better margins and less complexity.
          </p>
          <Button size="lg" asChild>
            <Link to="/auth/signup?type=agency">
              Get Started for Agencies <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="bg-muted/50 py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Vendor Relationship Chaos</h3>
              <p className="text-sm text-muted-foreground">
                Stop managing multiple vendor accounts, portals, and payment terms across clients.
              </p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Margin Pressure</h3>
              <p className="text-sm text-muted-foreground">
                Get competitive pricing without vendor network access—improve profitability on every job.
              </p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Project Complexity</h3>
              <p className="text-sm text-muted-foreground">
                Juggling multiple client jobs with different vendors, timelines, and requirements is overwhelming.
              </p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Client Transparency</h3>
              <p className="text-sm text-muted-foreground">
                Give clients real-time visibility into production status and delivery tracking.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Powerful Tools for Agencies</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Everything you need to manage client production efficiently
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard
            icon={Network}
            title="One Platform, Unlimited Vendors"
            description="Access our entire verified vendor marketplace through a single interface. No more multiple logins, portals, or vendor management headaches."
          />
          <FeatureCard
            icon={Building2}
            title="Multi-Client Dashboard"
            description="Manage all client production projects in one centralized hub with clear visibility and organization."
          />
          <FeatureCard
            icon={Shield}
            title="Verified Vendor Marketplace"
            description="Browse vendors with verified credentials and documented capabilities in print, packaging, promotional items, signage, and more."
          />
          <FeatureCard
            icon={GitCompare}
            title="Competitive Bidding"
            description="Get multiple quotes from verified marketplace vendors to compare pricing and improve your margins."
          />
          <FeatureCard
            icon={FileCheck}
            title="AI-Assisted Templates"
            description="Create specs through AI conversation. Save as templates for recurring client orders and reuse with any network vendor."
          />
          <FeatureCard
            icon={Eye}
            title="Client Portal Access"
            description="Optional client visibility into project status and milestones for enhanced transparency."
          />
          <FeatureCard
            icon={Clock}
            title="Milestone Tracking"
            description="Clear timelines and deliverables for every production job with all communication centralized."
          />
          <FeatureCard
            icon={DollarSign}
            title="Secure Payments"
            description="Protected payments safeguard your clients and ensure vendor performance—one payment system for all vendors."
          />
          <FeatureCard
            icon={CheckCircle}
            title="Quality Control"
            description="Delivery proof requirements with photo documentation before releasing payments."
          />
          <FeatureCard
            icon={TrendingUp}
            title="Margin Protection"
            description="Transparent pricing structure helps maintain healthy margins on client production work."
          />
          <FeatureCard
            icon={Users}
            title="Team Collaboration"
            description="Add team members with role-based access for account managers and production coordinators."
          />
          <FeatureCard
            icon={Zap}
            title="Simplified Onboarding"
            description="Need a specialized vendor? Invite them to join the platform instantly through our network."
          />
        </div>
      </section>

      {/* Network Benefit Callout */}
      <section className="border-y bg-primary/5 py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold">Stop Managing Vendors. Start Managing Projects.</h2>
            <p className="text-xl text-muted-foreground">
              Access a growing verified vendor marketplace through a single login with one unified payment process and support team.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">Verified</div>
                <div className="text-sm text-muted-foreground">Vendor Marketplace</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">1</div>
                <div className="text-sm text-muted-foreground">Login Portal</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">1</div>
                <div className="text-sm text-muted-foreground">Payment System</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-primary">0</div>
                <div className="text-sm text-muted-foreground">Platform Fees</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="container py-20">
        <h2 className="text-3xl font-bold text-center mb-12">Why Agencies Choose Us</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Better Margins</h3>
            <p className="text-sm text-muted-foreground">
              Competitive bidding and transparent pricing typically deliver 15-30% cost savings, directly improving your bottom line.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Massive Time Savings</h3>
            <p className="text-sm text-muted-foreground">
              One platform eliminates hours spent managing vendor portals, payment terms, and communication channels.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Network Scale, Single Interface</h3>
            <p className="text-sm text-muted-foreground">
              Access dozens of verified specialized vendors without the administrative burden of direct relationships.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-card">
            <h3 className="text-xl font-semibold mb-3">Client Satisfaction</h3>
            <p className="text-sm text-muted-foreground">
              Real-time visibility, on-time delivery, and quality control consistently impress your clients.
            </p>
          </div>
        </div>
      </section>

      {/* Agency Journey */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Agency Journey</h2>
          <div className="max-w-3xl mx-auto space-y-8">
            {[
              {
                step: "1",
                title: "Set Up Agency Profile",
                description: "Create your agency account and add team members with appropriate roles and permissions."
              },
              {
                step: "2",
                title: "Create RFQ with AI Assistance",
                description: "Describe the project and let AI guide you through creating professional specifications—no forms, just conversation."
              },
              {
                step: "3",
                title: "Platform Matches to Verified Vendors",
                description: "Our platform automatically identifies relevant verified vendors from the network based on your project criteria—or invite your preferred vendors."
              },
              {
                step: "4",
                title: "Review Bids in One Place",
                description: "Compare competitive bids from multiple verified vendors side-by-side with transparent line-item pricing."
              },
              {
                step: "5",
                title: "Award & Secure Payment",
                description: "Select the winning vendor and initiate secure protected payment—choose pass-through billing or agency payment."
              },
              {
                step: "6",
                title: "Track Production Centrally",
                description: "Monitor milestones, communicate with vendors, and keep clients updated—all through one platform."
              },
              {
                step: "7",
                title: "Review Delivery Proofs",
                description: "Approve vendor-submitted delivery documentation and photos to ensure quality before payment release."
              },
              {
                step: "8",
                title: "Archive for Future Orders",
                description: "Save project specifications as templates for recurring orders with any network vendor."
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
                  {item.step}
                </div>
                <div className="pt-2 space-y-2">
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="container py-20">
        <h2 className="text-3xl font-bold text-center mb-12">Perfect For</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Brand Collateral Production</h3>
            <p className="text-sm text-muted-foreground">
              Manage production of brochures, catalogs, and marketing materials for retail clients across multiple vendors.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Trade Shows & Events</h3>
            <p className="text-sm text-muted-foreground">
              Source booth materials, promotional items, and signage from specialized vendors for client events.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Packaging Production</h3>
            <p className="text-sm text-muted-foreground">
              Ongoing packaging production with quality control requirements and delivery verification.
            </p>
          </div>
          <div className="p-6 rounded-lg border bg-background">
            <h3 className="text-xl font-semibold mb-3">Multi-Location Rollouts</h3>
            <p className="text-sm text-muted-foreground">
              Coordinate signage, displays, and materials delivery across multiple client locations.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Transform Your Agency Production?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            One platform. Verified vendors. Better margins. Join agencies already simplifying production management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=agency">
                Get Started for Agencies
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/features">
                Explore Features
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
